import { z } from 'zod';
/**
 * Domain enumerations shared by the API and web app.
 *
 * NOTE: These are modeled as Zod enums (validated at the app layer) rather than
 * database-native enums so the schema stays portable between SQLite (dev) and
 * PostgreSQL (prod). See apps/api/prisma/schema.prisma.
 */
export const TicketType = z.enum([
    'customer_support',
    'internal_bug',
    'feature_request',
    'change_request',
    'task',
    'tech_debt',
    'epic',
    'subtask',
    // Infrastructure / DevOps work — kept off the feature & support boards so the
    // AWS-scorecard cleanup tickets (and other infra work) have their own lane.
    'infrastructure',
]);
/** Default workflow lifecycle (FR-103). Projects may override via workflow schemes. */
export const TicketStatus = z.enum([
    'draft',
    'submitted',
    'triage',
    'backlog',
    'design_needed',
    'acceptance_criteria_needed',
    'estimate_needed',
    'needs_approval',
    'ready_for_qa',
    'approved',
    'approved_for_agent',
    'in_progress',
    'agent_in_progress',
    'internal_waiting',
    'dev_done',
    'qa',
    // QA signed off on DAV; awaiting the manual develop -> uat merge. Distinct
    // from `dev_done`, which means "the agent finished, a human has not checked it".
    'ready_for_uat',
    'uat',
    'staging',
    'done',
    'cancelled',
    'rejected',
]);
/** Urgency axis, kept independent of severity (FR-105). */
export const Priority = z.enum(['lowest', 'low', 'medium', 'high', 'highest']);
/** Impact axis, kept independent of priority (FR-105). */
export const Severity = z.enum(['s1', 's2', 's3', 's4']);
/** Author kind for messages/conversation (FR-201). */
export const AuthorKind = z.enum([
    'internal_user',
    'customer_contact',
    'agent',
    'system',
    'integration',
]);
/** Message visibility. Customer-safe projection only ever returns `customer_visible` (FR-204, BR-003 #7). */
export const Visibility = z.enum([
    'internal',
    'customer_visible',
    'agent_only',
    'private_admin',
]);
/** Global roles (FR-701). super_admin outranks admin (manages admins + SSO). */
export const Role = z.enum([
    'super_admin',
    'admin',
    'lead',
    'cx',
    'developer',
    'qa',
    'support_agent',
    'product_owner',
    'read_only',
    'integration',
    'ai_agent',
]);
/** Ticket relationship types (FR-109). */
export const RelationshipType = z.enum([
    'blocks',
    'is_blocked_by',
    'duplicates',
    'is_duplicated_by',
    'relates_to',
    'clones',
    'is_cloned_by',
    'parent_of',
    'child_of',
    'caused_by',
    'follow_up',
    'regression_of',
]);
/** Agent attempt lifecycle (FR-509). */
export const AgentAttemptStatus = z.enum([
    'queued',
    'claimed',
    'analyzing',
    'implementing',
    'testing',
    'pr_created',
    'needs_review',
    'blocked',
    'failed',
    'done',
    'cancelled',
]);
/**
 * Sprint lifecycle (spec §5). `planned` is a legacy alias of `planning` kept so
 * pre-expansion data/seed continues to validate.
 */
export const SprintStatus = z.enum([
    'draft',
    'planning',
    'ready_to_start',
    'active',
    'review',
    'completed',
    'cancelled',
    'planned', // legacy alias of "planning"
]);
/** Capacity unit a sprint plans in (spec §4). */
export const CapacityUnit = z.enum(['points', 'hours', 'both']);
/** A ticket's commitment level within a sprint (spec §14). */
export const SprintCommitmentStatus = z.enum(['committed', 'stretch', 'added', 'removed']);
/** Sprint health signal (spec §19). */
export const SprintHealth = z.enum(['on_track', 'at_risk', 'off_track', 'blocked', 'completed']);
/** Reason a ticket spilled over at sprint close (spec §21). */
export const SpilloverReason = z.enum([
    'blocked',
    'underestimated',
    'dependency',
    'scope_changed',
    'not_started',
    'qa_pending',
    'customer_pending',
    'agent_pending',
]);
/**
 * Release lifecycle (spec §26). The trailing values (`planning`, `uat`,
 * `cx_review`, `approved`) are legacy statuses kept so pre-expansion data/seed
 * continues to validate.
 */
export const ReleaseStatus = z.enum([
    'draft',
    'scope_planning',
    'ready_for_uat',
    'uat_branch_created',
    'deployed_to_uat',
    'uat_review',
    'uat_approved',
    'ready_for_production',
    'production_scheduled',
    'deployed_to_production',
    'released',
    'blocked',
    'rolled_back',
    'cancelled',
    'hotfix_in_progress',
    // legacy values
    'planning',
    'uat',
    'cx_review',
    'approved',
]);
/** Release type / version classification (spec §25). */
export const ReleaseType = z.enum([
    'major',
    'minor',
    'regular',
    'patch',
    'hotfix',
    'bugfix',
    'emergency',
    'uat_candidate',
    'production',
]);
/** How a ticket entered a release scope (spec §30). */
export const ReleaseTicketSource = z.enum([
    'manual',
    'ql',
    'sprint',
    'pr',
    'status',
    'fix_version',
    'date_range',
    'import',
]);
/** Release gate evaluation status (spec §32). */
export const ReleaseGateStatus = z.enum([
    'passed',
    'failed',
    'warning',
    'not_applicable',
    'not_checked',
]);
/** UAT reviewer role (spec §33). */
export const ReleaseReviewerType = z.enum([
    'required_approver',
    'optional_reviewer',
    'customer_reviewer',
    'qa_reviewer',
    'cx_reviewer',
    'product_owner',
    'engineering_lead',
]);
/** UAT reviewer response status (spec §33). */
export const ReleaseReviewerStatus = z.enum([
    'pending',
    'viewed',
    'approved',
    'rejected',
    'changes_requested',
    'no_response',
]);
/** Deployment target environment (spec §37). */
export const DeploymentEnvironment = z.enum(['uat', 'staging', 'production']);
/** Deployment record status (spec §37). */
export const DeploymentStatus = z.enum([
    'pending',
    'in_progress',
    'succeeded',
    'failed',
    'rolled_back',
]);
/** Release health signal (spec §39). */
export const ReleaseHealth = z.enum([
    'on_track',
    'at_risk',
    'blocked',
    'ready_for_uat',
    'in_uat',
    'ready_for_production',
    'released',
    'rolled_back',
]);
/** API key scopes preserved from the legacy intake contract (BR-003 #6). */
export const ApiScope = z.enum([
    'intake:read',
    'intake:write',
    'customers:read',
    'customers:write',
    'promises:read',
    'promises:write',
    'requests:claim',
    'requests:transition',
    'requests:comment',
    'requests:assign',
    'requests:type',
    'artifacts:write',
    // Read the screenshot work queue / write release-note evidence back.
    'release-notes:read',
    'release-notes:write',
    'metrics:read',
    // Write the derived third-party-risk inventory + findings (weekly scanner).
    'risk:write',
]);
export const TicketSource = z.enum([
    'ui',
    'api',
    'customer_portal',
    'email',
    'webhook',
    'ai_braindump',
    'agent',
    'import',
    'hubspot',
]);
/**
 * Why an internal bug happened (weekly report v5). Set by a person on the
 * ticket (`bugOutcome`) or suggested by the AI (`aiBugOutcome`). Everything
 * except `actual_bug` is "not a bug".
 */
export const BugOutcome = z.enum([
    'actual_bug',
    'user_error',
    'training',
    'configuration',
    'duplicate',
    'no_fault_found',
    'works_as_designed',
]);
//# sourceMappingURL=enums.js.map